<template>
  <div :style="{ height: height + 'px', width: '100%' }">
    <Radar :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'vue-chartjs';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface DataPoint {
  subject: string;
  value: number;
  fullMark?: number;
  previous?: number;
}

const props = withDefaults(
  defineProps<{
    data: DataPoint[];
    showPrevious?: boolean;
    height?: number;
  }>(),
  {
    showPrevious: false,
    height: 300,
  },
);

const chartData = computed(() => {
  const labels = props.data.map((d) => d.subject);

  const datasets = [];

  // "Anterior" dataset (previous eval overlay) -- rendered first so it sits behind
  if (props.showPrevious) {
    datasets.push({
      label: 'Anterior',
      data: props.data.map((d) => d.previous ?? 0),
      borderColor: 'rgba(61, 55, 50, 0.12)',
      borderWidth: 1.5,
      borderDash: [4, 4],
      backgroundColor: 'rgba(61, 58, 54, 0.05)',
      pointBackgroundColor: 'rgba(61, 55, 50, 0.12)',
      pointBorderColor: 'rgba(61, 55, 50, 0.12)',
      pointRadius: 2,
    });
  }

  // "Actual" dataset -- gold color
  datasets.push({
    label: 'Actual',
    data: props.data.map((d) => d.value),
    borderColor: 'var(--color-aged-gold)',
    borderWidth: 2,
    backgroundColor: 'rgba(201, 169, 110, 0.15)',
    pointBackgroundColor: 'var(--color-aged-gold)',
    pointBorderColor: 'var(--color-aged-gold)',
    pointRadius: 3,
  });

  return { labels, datasets };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: props.showPrevious,
      labels: {
        color: 'var(--color-charcoal-mist)',
        font: { size: 11 },
      },
    },
    tooltip: {
      backgroundColor: 'var(--color-sandy-beige)',
      titleColor: 'var(--color-deep-charcoal)',
      bodyColor: 'var(--color-charcoal-mist)',
      borderColor: 'rgba(61, 55, 50, 0.12)',
      borderWidth: 1,
    },
  },
  scales: {
    r: {
      min: 0,
      max: 10,
      ticks: {
        stepSize: 2,
        color: 'rgba(160, 152, 144, 0.3)',
        backdropColor: 'transparent',
        font: { size: 10 },
      },
      grid: {
        color: 'rgba(61, 58, 54, 0.1)',
      },
      angleLines: {
        color: 'rgba(61, 58, 54, 0.1)',
      },
      pointLabels: {
        color: 'var(--color-charcoal-mist)',
        font: { size: 11 },
      },
    },
  },
}));
</script>
